/**
 * AddBidCampaignModal — create or edit a Bid Campaign (Supabase).
 *
 * Opens via CustomEvent('open-add-bid-campaign-modal', { detail }):
 *   create from milestone: { projectId, milestoneAirtableId }
 *   create from Construction: { projectId }  (milestoneAirtableId absent)
 *   edit: { campaignId, prefill: { round_number, status, invitation_date, bid_due_date,
 *                                  build_start_target_date, pre_bid_job_walk, permit_disposition } }
 *
 * On create: auto-computes round number; links to milestoneAirtableId if provided.
 * On success busts ctx.bidCampaignCache + ctx.msBidCampaignCache and dispatches outline-render.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { createCampaign, updateCampaign, linkMilestone, CAMPAIGN_STATUS_LABELS } from '../services/bidCampaigns.js'
import Select from './Select.jsx'

const PERMIT_DISPOSITIONS = ['Permits are Ready', 'Permits are in Progress']
const STATUS_OPTIONS = Object.keys(CAMPAIGN_STATUS_LABELS).map(v => ({ value: v, label: CAMPAIGN_STATUS_LABELS[v] }))

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none',
}

export default function AddBidCampaignModal() {
  const [open,       setOpen]       = useState(false)
  const [projectId,  setProjectId]  = useState(null)
  const [msAirtableId, setMsAirtableId] = useState(null)
  const [editId,     setEditId]     = useState(null)
  const [round,      setRound]      = useState('')
  const [status,     setStatus]     = useState('preview')
  const [invDate,    setInvDate]    = useState('')
  const [dueDate,    setDueDate]    = useState('')
  const [buildDate,  setBuildDate]  = useState('')
  const [jobWalk,    setJobWalk]    = useState('')
  const [disposition,setDisposition]= useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setProjectId(d.projectId || null)
      setMsAirtableId(d.milestoneAirtableId || null)
      setEditId(d.campaignId || null)
      const p = d.prefill || {}
      // Round number: from prefill on edit, else auto-compute from existing campaigns
      let nextRound = p.round_number != null ? String(p.round_number) : ''
      if (!d.campaignId && nextRound === '') {
        const existing = Array.isArray(ctx.bidCampaignCache) ? ctx.bidCampaignCache : []
        nextRound = String((existing.reduce((m, c) => Math.max(m, c.round_number || 0), 0)) + 1)
      }
      setRound(nextRound)
      setStatus(p.status || 'preview')
      setInvDate(p.invitation_date || '')
      setDueDate(p.bid_due_date || '')
      setBuildDate(p.build_start_target_date || '')
      setJobWalk(p.pre_bid_job_walk || '')
      setDisposition(p.permit_disposition || '')
      setError(null); setSubmitting(false); setOpen(true)
    }
    window.addEventListener('open-add-bid-campaign-modal', onOpen)
    return () => window.removeEventListener('open-add-bid-campaign-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    setSubmitting(true); setError(null)
    const fields = {
      round_number: round ? parseInt(round, 10) : null,
      status,
      invitation_date: invDate || null,
      bid_due_date: dueDate || null,
      build_start_target_date: buildDate || null,
      pre_bid_job_walk: jobWalk || null,
      permit_disposition: disposition || null,
    }
    try {
      if (editId) {
        await updateCampaign(editId, fields)
      } else {
        if (!projectId) { setError('No project in context.'); setSubmitting(false); return }
        const campaign = await createCampaign(projectId, fields)
        // Link to the milestone that triggered the creation (if opened from a milestone ⋯ menu)
        if (campaign && campaign.id && msAirtableId) {
          await linkMilestone(campaign.id, msAirtableId, 'primary')
          if (ctx.msBidCampaignCache) ctx.msBidCampaignCache[msAirtableId] = undefined
        }
      }
      ctx.bidCampaignCache = undefined
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save campaign'); setSubmitting(false)
    }
  }

  if (!open) return null
  const canSubmit = !submitting

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddBidCampaignModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 440, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            {editId ? 'Edit Bid Campaign' : 'Add Bid Campaign'}
          </span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ width: 120 }}>
              <label style={labelStyle}>Round #</label>
              <input type="number" style={inputStyle} value={round} onChange={e => setRound(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Status</label>
              <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} style={{ width: '100%', fontSize: 12 }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Invitation Date</label>
              <input type="date" style={inputStyle} value={invDate} onChange={e => setInvDate(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Bid Due Date</label>
              <input type="date" style={inputStyle} value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Pre-Bid Job Walk</label>
              <input type="date" style={inputStyle} value={jobWalk} onChange={e => setJobWalk(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Build Start Target</label>
              <input type="date" style={inputStyle} value={buildDate} onChange={e => setBuildDate(e.target.value)} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Permit Disposition</label>
            <Select value={disposition} onChange={setDisposition}
              options={[{ value: '', label: '— None —' }].concat(PERMIT_DISPOSITIONS.map(d => ({ value: d, label: d })))}
              placeholder="— None —" style={{ width: '100%', fontSize: 12 }} />
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
              {submitting ? 'Saving…' : (editId ? 'Save' : 'Add Campaign')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
